
import React, { useState } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import Icon from "@mui/material/Icon"; // For generic icons if needed
import AssessmentIcon from "@mui/icons-material/Assessment";
import GroupIcon from "@mui/icons-material/Group";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import SentimentSatisfiedAltIcon from "@mui/icons-material/SentimentSatisfiedAlt";
import ArticleIcon from "@mui/icons-material/Article";
import PublicIcon from "@mui/icons-material/Public";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import { charts, checkChartRequirements } from "layouts/insight-hub";

const categories = [
    { id: "Audience", title: "Audience", description: "Deep dive into your follower growth and trends.", icon: <GroupIcon fontSize="large" color="inherit" /> },
    { id: "Engagement", title: "Engagement", description: "Analysis of interactions and engagement rates.", icon: <ThumbUpIcon fontSize="large" color="inherit" /> },
    { id: "Sentiment", title: "Sentiment", description: "Understanding the sentiment of comments and feedback.", icon: <SentimentSatisfiedAltIcon fontSize="large" color="inherit" /> },
    { id: "Content", title: "Content", description: "Performance of your content pieces.", icon: <ArticleIcon fontSize="large" color="inherit" /> },
    { id: "Demographics", title: "Demographics", description: "Breakdown of your audience by age, gender, and location.", icon: <PublicIcon fontSize="large" color="inherit" /> },
];

function CategoryCard({ category, isSelected, onSelect }) {
    return (
        <Card
            onClick={() => onSelect(category.id)}
            sx={{
                cursor: "pointer",
                position: "relative",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                p: 3,
                textAlign: "center",
                border: (theme) => `2px solid ${isSelected ? theme.palette.info.main : "transparent"}`,
                boxShadow: isSelected ? 6 : 1,
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: 4,
                    borderColor: (theme) => !isSelected && theme.palette.grey[300],
                },
            }}
        >
            {/* ✅ Checkmark Icon */}
            {isSelected && (
                <MDBox
                    sx={{
                        position: "absolute",
                        top: 10,
                        right: 10,
                        color: "warning.main", // Golden/Orange color
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 1,
                    }}
                >
                    <CheckCircleIcon sx={{ fontSize: 24 }} />
                </MDBox>
            )}

            <MDBox
                sx={{
                    color: isSelected ? "info.main" : "text.secondary",
                    mb: 2,
                }}
            >
                {category.icon}
            </MDBox>
            <MDTypography variant="h6" fontWeight="bold" gutterBottom>
                {category.title}
            </MDTypography>
            <MDTypography variant="button" color="text" fontWeight="regular">
                {category.description}
            </MDTypography>
        </Card>
    );
}

function Step2CategorySelection({ selectedCategories = [], onSelect, selectedProfiles = [] }) {
    // 🚀 Filter Categories based on selected profiles capabilities
    const validCategories = categories.filter(category => {
        // 1. Summary is always available
        if (category.id === "Summary") return true;

        // 2. Check if ANY chart in this category is valid for the current profiles
        // We use the same helper as Insight Hub
        const categoryCharts = charts.filter(c => c.category === category.id);
        const hasValidChart = categoryCharts.some(c => checkChartRequirements(c, selectedProfiles));

        return hasValidChart;
    });

    return (
        <Card>
            <MDBox>
                <MDBox
                    mx={2}
                    mt={-3}
                    py={1}
                    px={2}
                    variant="gradient"
                    bgColor="info"
                    borderRadius="lg"
                    coloredShadow="info"
                    mb={3}
                >
                    <MDTypography variant="h6" color="white">
                        Choose Report Category
                    </MDTypography>
                </MDBox>

                <MDBox px={2} py={3}>
                    {validCategories.length === 0 ? (
                        <MDBox textAlign="center" py={5}>
                            <MDTypography variant="h5" color="text">
                                No valid categories available for the selected profiles.
                            </MDTypography>
                        </MDBox>
                    ) : (
                        <Grid container spacing={3}>
                            {validCategories.map((category) => (
                                <Grid item xs={12} sm={6} md={4} key={category.id}>
                                    <CategoryCard
                                        category={category}
                                        isSelected={selectedCategories.includes(category.id)}
                                        onSelect={onSelect}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    )}
                </MDBox>
            </MDBox>
        </Card>
    );
}

export default Step2CategorySelection;
